import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { atom } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "src/i18n";
import { Planogram } from "src/components/Planogram/Planogram";
import { Box, Color, Flex, SubgridTable } from "src/elements";
import { getNewItemLabel } from "src/components/Planogram/common/planogramItemView";
import { SubgridCell } from "src/elements/SubgridTable/components/SubgridCell";
import { SubgridRowElement } from "src/elements/SubgridTable/components/SubgridRow";
import type { SubgridColumn } from "src/elements/SubgridTable/store/types";
import { dateIsoStringToLocaleString } from "src/utils";
import styled from "styled-components";
import {
  DEFAULT_NOTCH_RULER_WIDTH,
  getPdfPlanogramWidth,
} from "src/components/Planogram/components/Ruler/PdfNotchRulers";
import { LandscapeA4HeightInPx } from "./constant";
import { PageHeader } from "./PageHeader";
import type { StoreDetail } from "./PageMerchflows_ExportPdf_StorePogId";
import { createSingleBayPog, sanitizeStoreTitle } from "./singleBayUtils";
import type { A4Page, A4PageItem } from "./types";

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${LandscapeA4HeightInPx};
  border: 1px solid black;
  user-select: none;
  box-sizing: border-box;
  background-color: white;
  page-break-after: always;
  break-after: page;
`;

const FileNameHeader = styled.div`
  height: 40px;
  flex-shrink: 0;
  border-bottom: 1px solid black;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ReportTitle = styled.div`
  font-size: 11px;
  font-weight: bold;
`;

const StoreName = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const WrappedText = styled.div`
  overflow-wrap: anywhere;
  white-space: normal;
  width: 100%;
`;

const toTitleCase = (str: string | undefined | null) => {
  if (!str) return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const columns: SubgridColumn<A4PageItem>[] = [
  { id: "pi", header: "", width: "32px", headerPadding: "0 8px" },
  { id: "no", header: "No", width: "24px", headerPadding: "0 8px" },
  { id: "productCode", header: "Product code", width: "76px", headerPadding: "0 8px" },
  { id: "name", header: "Name", width: "minmax(0, 1fr)", headerPadding: "0 8px" },
  {
    id: "orientation",
    header: "Orientation",
    width: "68px",
    headerPadding: "0 8px",
  },
  {
    id: "merchStyle",
    header: "Merch style",
    width: "51px",
    headerPadding: "0 8px",
  },
  { id: "hFac", header: "H fac", width: "36px", headerPadding: "0 8px" },
  { id: "vFac", header: "V fac", width: "36px", headerPadding: "0 8px" },
  { id: "dFac", header: "D fac", width: "36px", headerPadding: "0 8px" },
  {
    id: "shelfStockCapacity",
    header: "Shelf stock capacity",
    width: "76px",
    headerPadding: "0 8px",
  },
];

const FooterContainer = styled(Flex)`
  width: 100%;
  height: 48px;
  flex-shrink: 0;
  align-items: center;
  box-sizing: border-box;
  background-color: white;
  border-top: 1px solid black;

  & > * {
    border-right: 1px solid black;
  }
  & > *:last-child {
    border-right: none;
  }
`;

const FooterColumn = styled(Flex)`
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 4px;
  padding-left: 8px;
  height: 100%;
  flex-grow: 1;
  flex-basis: 0;
  box-sizing: border-box;
`;

const ColumnTitle = styled.div`
  font-size: 8px;
  font-weight: bold;
  color: #555;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const ColumnValue = styled.div`
  font-size: 10px;
  font-weight: bold;
`;

const ShelfHeaderRow = styled.div`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
`;

export interface PdfFooterProps {
  storeDetail: StoreDetail | null;
  segment: string;
  pageNumber: number;
  totalPages: number;
}

export const PdfFooter = ({ storeDetail, segment, pageNumber, totalPages }: PdfFooterProps) => {
  const { t } = useTranslation("common");
  const dateEffective = storeDetail?.go_live_date
    ? dateIsoStringToLocaleString(storeDetail.go_live_date, "ddd MMM DD YYYY", true)
    : "";

  return (
    <FooterContainer>
      <FooterColumn>
        <ColumnTitle>{t("preparedBy")}</ColumnTitle>
        <ColumnValue></ColumnValue>
      </FooterColumn>
      <FooterColumn>
        <ColumnTitle>{t("approvedBy")}</ColumnTitle>
        <ColumnValue></ColumnValue>
      </FooterColumn>
      <FooterColumn>
        <ColumnTitle>{`${t("categoryOwner")} 1`}</ColumnTitle>
        <ColumnValue></ColumnValue>
      </FooterColumn>
      <FooterColumn>
        <ColumnTitle>{`${t("categoryOwner")} 2`}</ColumnTitle>
        <ColumnValue></ColumnValue>
      </FooterColumn>
      <FooterColumn>
        <ColumnTitle>{t("dateEffective")}</ColumnTitle>
        <ColumnValue>{dateEffective}</ColumnValue>
      </FooterColumn>
      <FooterColumn>
        <ColumnTitle>{t("segment")}</ColumnTitle>
        <ColumnValue>{segment}</ColumnValue>
      </FooterColumn>
      <FooterColumn>
        <ColumnTitle>{t("page")}</ColumnTitle>
        <ColumnValue>{`${pageNumber} / ${totalPages}`}</ColumnValue>
      </FooterColumn>
    </FooterContainer>
  );
};

type PlanogramPageRendererProps = {
  storeDetail: StoreDetail | null;
  bayId: string;
  totalSection: number;
  page: A4Page;
  section: number;
  slide: number;
  scaleX: number;
  scaleY: number;
  pog: Pog;
  pageNumber: number;
  totalPages: number;
  layoutIndex?: number;
};
export const SingleBayPage = ({
  storeDetail,
  bayId,
  page,
  section,
  scaleX,
  scaleY,
  pog,
  pageNumber,
  totalPages,
  layoutIndex = 0,
}: PlanogramPageRendererProps) => {
  const { t } = useTranslation("common");
  const oneBayPog = useMemo(
    () => createSingleBayPog({ pog, bayId, section }),
    [pog, bayId, section],
  );

  const oneBayPogAtom = useMemo(() => atom<Pog | null>(oneBayPog), [oneBayPog]);

  const formattedTitle = sanitizeStoreTitle(
    storeDetail?.category_code,
    storeDetail?.store,
    storeDetail?.template_id,
  );

  return (
    <PageContainer>
      {storeDetail && <PageHeader storeDetail={storeDetail} />}
      {storeDetail && (
        <FileNameHeader>
          <ReportTitle>{t("shelfSemanticReport")}</ReportTitle>
          <StoreName>{formattedTitle}</StoreName>
        </FileNameHeader>
      )}
      <Flex flexGrow={1} minHeight="0">
        <Flex
          position="relative"
          borderRight="1px solid black"
          width="40%"
          overflow="hidden"
          justify="center"
          align="center"
        >
          <Box
            position="relative"
            width={`${
              getPdfPlanogramWidth(
                oneBayPog.planogram.getWidth(),
                oneBayPog.planogram.bays.length,
                DEFAULT_NOTCH_RULER_WIDTH,
              ) * (scaleX ?? 1)
            }px`}
            height="100%"
          >
            {scaleX && scaleY && (
              <Planogram
                pogAtom={oneBayPogAtom}
                isEditable={false}
                isPog={true}
                pdfMode={true}
                customItemLabelOnly={true}
                customLabelRule={getNewItemLabel}
                categoryName={storeDetail?.category_code}
                scaleX={scaleX}
                scaleY={scaleY}
                pdfShelfLabelBayNo={section}
              />
            )}
          </Box>
        </Flex>
        <Box
          width="60%"
          height="100%"
          minHeight="0"
          overflow="hidden"
          data-pdf-bay-table={layoutIndex}
        >
          <SubgridTable
            columns={columns}
            data={page}
            rowRenderer={(row) => {
              if (row.type === "ITEM") {
                const hFac = row.item.getHorizontalFacings();
                const vFac = row.item.getVerticalFacings();
                const dFac = row.item.getDepthFacings();
                const capacity = row.item.getCapacity();
                const piIndicator = row.item.newItemFlag ? "PI" : "";
                return (
                  <SubgridRowElement background={Color.greenSmoke} data-pdf-row="">
                    <SubgridCell leftBorder justify="center">
                      {piIndicator}
                    </SubgridCell>
                    <SubgridCell>{row.no}</SubgridCell>
                    <SubgridCell>{row.item.productCode}</SubgridCell>
                    <SubgridCell>
                      <WrappedText>{row.item.name || ""}</WrappedText>
                    </SubgridCell>
                    <SubgridCell>{toTitleCase(row.item.orientation)}</SubgridCell>
                    <SubgridCell>{toTitleCase(row.item.merchandisingStyle)}</SubgridCell>
                    <SubgridCell>{hFac ?? "-"}</SubgridCell>
                    <SubgridCell>{vFac ?? "-"}</SubgridCell>
                    <SubgridCell>{dFac ?? "-"}</SubgridCell>
                    <SubgridCell>{capacity ?? "-"}</SubgridCell>
                  </SubgridRowElement>
                );
              }

              const formattedShelfName = `${t("shelf")} ${row.shelfName}`;
              const shelf: PlanogramShelf = row.shelf;
              const fromFloorStr = (shelf.getYBottom() / 100).toFixed(2);
              const widthStr = (shelf.width / 100).toFixed(2);
              const depthStr = (shelf.depth / 100).toFixed(2);
              const notchStr = shelf.getNotchNo();

              return (
                <ShelfHeaderRow data-pdf-row="">
                  <SubgridCell
                    span={3}
                    leftBorder={true}
                    background={Color.greenSmokeActive}
                    variant="h6"
                  >
                    {formattedShelfName}
                  </SubgridCell>
                  <SubgridCell span={1} background={Color.greenSmokeActive} variant="h6">
                    From Floor : {fromFloorStr} cm
                  </SubgridCell>
                  <SubgridCell span={2} background={Color.greenSmokeActive} variant="h6">
                    Width : {widthStr} cm
                  </SubgridCell>
                  <SubgridCell span={3} background={Color.greenSmokeActive} variant="h6">
                    Depth : {depthStr} cm
                  </SubgridCell>
                  <SubgridCell span={1} background={Color.greenSmokeActive} variant="h6">
                    Notch : {notchStr}
                  </SubgridCell>
                </ShelfHeaderRow>
              );
            }}
          />
        </Box>
      </Flex>
      <PdfFooter
        storeDetail={storeDetail}
        segment={String(section)}
        pageNumber={pageNumber}
        totalPages={totalPages}
      />
    </PageContainer>
  );
};
